// @flow
import { forwardRef, ReactNode, useEffect, useImperativeHandle, useRef } from 'react'
import { Form as FormikForm, useFormikContext } from 'formik'
import { difference } from 'lodash'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import ScrollToError from './ScrollToError'

export interface ErrorSource {
  pointer: string
}

export interface APIFieldError {
  detail?: string
  source?: ErrorSource
}

interface Props {
  children: ReactNode
  errors?: APIFieldError[]
}

const arrayDiff = (arr1: any[], arr2: any[]) => difference(arr1, arr2).length > 0

const useDeepCompareMemoize = (value: any[], comparisonFn: (array1: any[], array2: any[]) => boolean) => {
  const ref = useRef(value)
  if (comparisonFn(value, ref.current)) {
    ref.current = value
  }
  return ref.current
}

const kebabToSnake = (string: string) => string.replace(/-/g, '_')

const parseApiError = ({ detail = '', source = { pointer: '' } }: APIFieldError): [string | null, string] => {
  const { pointer } = source
  const attributeRegex = /\/data\/attributes\/([a-z_-]*)$/i
  const match = pointer?.match(attributeRegex)
  const fieldName = (match && match[1]) || null
  return [fieldName && kebabToSnake(fieldName), detail]
}

export interface FormRef {
  isDirty: () => boolean
}

const Form = forwardRef<FormRef, Props>(({ children, errors = [] }: Props, ref) => {
  const { setFieldError, dirty } = useFormikContext()
  const diffedErrors = useDeepCompareMemoize(errors, arrayDiff)

  useImperativeHandle(ref, () => ({
    isDirty: () => dirty,
  }))

  useEffect(() => {
    let resolved = 0
    diffedErrors.forEach((error) => {
      const [name, msg] = parseApiError(error)
      if (name) {
        setFieldError(name, msg)
        resolved += 1
      }
    })
    if (resolved === 0 && diffedErrors.length > 0) {
      // there were errors unable to be attributed to any particular field
      showErrorMessageToast('There was an error updating the profile. Our team has been notified.')
    }
  }, [diffedErrors]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormikForm>
      <ScrollToError>{children}</ScrollToError>
    </FormikForm>
  )
})

export default Form
